// src/components/VideoPlayerModal.js
// Video player modal — plays workout videos in a fullscreen overlay
// Uses native <video> + HLS for AirPlay/Chromecast support when hlsUrl is available
// Falls back to Bunny Stream iframe embed when hlsUrl is not available
// Safari plays HLS natively; Chrome/Firefox use hls.js library

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ClassComments from './ClassComments';
import CompletionHistoryList from './CompletionHistoryList';
import { markWorkoutComplete, fetchVideoCompletions } from '../services/firebase';
import { flushWatchProgress } from '../services/videoWatchProgress';
import { subscribeCastSession } from '../services/castSession';
import { subscribeBunnyProgress } from '../services/bunnyPlayerBridge';
import { relativeDayLabel } from '../utils/timezone';
import { useScreenWakeLock } from '../utils/useScreenWakeLock';

const HLS_JS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.7/hls.min.js';
const MUX_PLAYER_CDN = 'https://cdn.jsdelivr.net/npm/@mux/mux-player@3/dist/mux-player.min.js';

// Auto-complete threshold for class recordings — see docs/decisions/adr-auto-completion.md
const AUTO_COMPLETE_THRESHOLD = 0.75;
// On-demand watch progress flush cadence — see docs/decisions/adr-on-demand-watch-tracking.md
const WATCH_PROGRESS_HEARTBEAT_MS = 60000;        // flush every 60s of accumulated play
const WATCH_PROGRESS_MIN_DELTA_SEC = 5;            // skip flushes below this — saves writes on pure pauses

function VideoPlayerModal({ video, onClose, vertical = false, isFavorited = false, onToggleFavorite, favoriteCount = 0, autoplay = true, userId, userName, isAdmin = false, onAutoComplete, getToken, highlightCommentId = null, completionCount = 0, lastCompletedAt = null, completionCopy = {}, programId = null, onUndoComplete, completionSource = 'program', showLength = true, renderTogether }) {
    // Manual "Log this workout" confirmation for THIS modal-open (event-log model).
    const [manualLogged, setManualLogged] = useState(false);
    // Persistent "Your history" disclosure — per-entry completion removal (see
    // docs/ux/completion-undo.md). Ids resolved just-in-time on expand so the
    // auto-complete write path is never touched.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyList, setHistoryList] = useState(null); // null = not yet loaded
    const [historyLoading, setHistoryLoading] = useState(false);
    // Scroll hint is gated on actual overflow — it only makes sense to prompt
    // the viewer to "scroll for details" when there IS content below the fold.
    // Default false; the effect below flips it on only if scrollHeight exceeds
    // clientHeight. Re-measures on resize (Mux Player settling, comments loading,
    // viewport changes) so a late-growing panel still triggers the hint.
    const [showScrollHint, setShowScrollHint] = useState(false);
    const scrollContainerRef = useRef(null);
    const [hlsReady, setHlsReady] = useState(false);
    const [hlsFailed, setHlsFailed] = useState(false);
    // Drives the screen Wake Lock (below) — true only while the media element
    // is actually playing. Keeps the OS from sleeping/evicting the PWA during a
    // workout, which is what bounces members to the landing page mid-stream.
    const [isPlaying, setIsPlaying] = useState(false);
    useScreenWakeLock(isPlaying);
    const videoRef = useRef(null);
    // ref attached to the inline <mux-player> element for class recordings, used by
    // the auto-complete effect below to subscribe to timeupdate. Separate from
    // videoRef (which targets the native HLS <video>) so each playback path can
    // attach its listener cleanly.
    const muxPlayerRef = useRef(null);
    // ref on the Bunny iframe (program/free fallback path) for the Player.js
    // progress bridge that powers program auto-completion. See bunnyPlayerBridge.js.
    const iframeRef = useRef(null);
    // One-shot guard: once we've crossed the auto-complete threshold during this
    // modal-open, never fire again (resets when video.id changes — i.e., user
    // closed the modal and opened a different recording). See
    // docs/decisions/adr-auto-completion.md §2.6.
    const autoCompleteFiredRef = useRef(false);
    // Cast tracking (Chromecast/AirPlay). When casting, the local element goes
    // idle so the timeupdate accumulator + position-based auto-complete both
    // stop. `castConnectedRef` keeps the local and cast accumulators mutually
    // exclusive; `castEverConnectedRef` gates the time-based completion so a
    // NON-cast session's completion stays exactly the position-based path;
    // `cumulativeWatchedRef` is total watched seconds this modal-open (local +
    // cast) used only for the cast completion threshold. See
    // docs/decisions/adr-cast-aware-watch-tracking.md.
    const castConnectedRef = useRef(false);
    const castEverConnectedRef = useRef(false);
    const cumulativeWatchedRef = useRef(0);
    // Per-modal-open occurrence token (see adr-completions-event-log.md). Completions
    // are an event log: each playback session logs AT MOST one completion (the
    // shared autoCompleteFiredRef + this key dedupe within the session), while a
    // later session (reopen / next day) gets a fresh key → a genuine new credit.
    // This is what lets a re-watch count toward streaks/challenges.
    const sessionOccurrenceKeyRef = useRef(null);
    useEffect(() => {
        sessionOccurrenceKeyRef.current = video?.id ? `${video.id}_${Date.now()}` : null;
        setManualLogged(false);
        // Reset the "Your history" disclosure for the newly-opened video.
        setHistoryOpen(false);
        setHistoryList(null);
        setHistoryLoading(false);
    }, [video?.id]);

    // Toggle + lazy-load the per-entry history list (just-in-time id resolution).
    const toggleHistory = async () => {
        if (historyOpen) { setHistoryOpen(false); return; }
        setHistoryOpen(true);
        if (historyList === null && !historyLoading && userId && video?.id) {
            setHistoryLoading(true);
            const list = await fetchVideoCompletions(userId, video.id, programId);
            setHistoryList(list);
            setHistoryLoading(false);
        }
    };

    // Remove ONE specific completion. Delegates the delete + count decrement to
    // the parent (onUndoComplete), then splices the local list. Optimistic with
    // rollback: if the parent reports failure, the row is kept and a retry shows.
    const handleRemoveEntry = async (entryId) => {
        if (typeof onUndoComplete !== 'function' || !video?.id) return false;
        let ok = false;
        try { ok = await onUndoComplete(video, entryId); } catch (_) { ok = false; }
        if (ok) setHistoryList((prev) => (prev || []).filter((e) => e.id !== entryId));
        return ok;
    };

    // Manual "Log this workout" — the seamless fallback when ≥75% auto-credit
    // doesn't land (watched on another device, left briefly, scrubbed, etc.).
    // CRITICAL: it shares autoCompleteFiredRef + sessionOccurrenceKeyRef with the
    // auto path, so manual + auto in the SAME sitting collapse to exactly ONE
    // completion (and one onAutoComplete bump) — a re-open or next day logs anew.
    const handleManualLog = () => {
        setManualLogged(true);
        if (autoCompleteFiredRef.current) return; // already credited this session
        autoCompleteFiredRef.current = true;
        // `programId || null`: On Demand class recordings pass no programId (→ null,
        // unchanged); an in-app recorded PROGRAM video passes its program.id so the
        // manual log threads to the program. Additive — null path is byte-identical.
        markWorkoutComplete(userId, video.id, video.title || '', programId || null, {
            occurrenceKey: sessionOccurrenceKeyRef.current,
            source: 'manual',
        }).catch(() => {});
        if (typeof onAutoComplete === 'function') {
            try { onAutoComplete(video.id); } catch (_) { /* defensive */ }
        }
    };

    // ── Share / copy-link (class recordings only) ──────────────────────────
    // Copies the canonical PUBLIC class link (/class/<id>) — the one shareable
    // URL that works for everyone: strangers watch (if free) and convert,
    // members get handed into the app. Clipboard API first, legacy execCommand
    // fallback for non-secure/older contexts; never throws. The "Copied" pill
    // clears after a beat — timer cleaned up on unmount.
    const [copied, setCopied] = useState(false);
    const copyTimerRef = useRef(null);
    useEffect(() => () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current); }, []);
    const handleShareLink = async () => {
        if (!video?.id) return;
        const link = `${window.location.origin}/class/${encodeURIComponent(video.id)}`;
        const showCopied = () => {
            setCopied(true);
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => setCopied(false), 1800);
        };
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(link);
                showCopied();
                return;
            }
        } catch (_) { /* fall through to legacy path */ }
        try {
            const ta = document.createElement('textarea');
            ta.value = link;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            showCopied();
        } catch (_) { /* give up silently — never throw */ }
    };

    // Load Mux Player for class recordings
    useEffect(() => {
        if (!video?.isClassRecording || !video?.muxPlaybackId) return;
        if (customElements.get('mux-player')) return;
        const script = document.createElement('script');
        script.src = MUX_PLAYER_CDN;
        script.async = true;
        document.head.appendChild(script);
    }, [video?.isClassRecording, video?.muxPlaybackId]);
    const hlsRef = useRef(null);

    // "How did it feel?" (ClassTogether finish moment): open the comments if they
    // are collapsed, bring them into view, and put the cursor in the box. The
    // collapse toggle is the first button inside the comments block.
    const commentsWrapRef = useRef(null);
    const openComments = useCallback(() => {
        const el = commentsWrapRef.current;
        if (!el) return;
        if (!el.querySelector('textarea')) {
            const toggle = el.querySelector('button');
            if (toggle) toggle.click();
        }
        setTimeout(() => {
            const box = el.querySelector('textarea');
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (box) box.focus({ preventScroll: true });
        }, 150);
    }, []);

    // Overflow-aware scroll hint: measure on layout and on resize. If the
    // scroll container is taller than the viewport (body + comments + video
    // combined), show the hint briefly. Otherwise leave it hidden.
    useLayoutEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        let fadeTimer = null;
        let didShow = false;
        const OVERFLOW_THRESHOLD_PX = 20; // avoid sub-pixel false-positives

        const check = () => {
            if (didShow) return; // one show per modal open; don't re-trigger after fade
            const overflow = el.scrollHeight - el.clientHeight;
            const hasScrolled = el.scrollTop > 0;
            if (overflow > OVERFLOW_THRESHOLD_PX && !hasScrolled) {
                didShow = true;
                setShowScrollHint(true);
                fadeTimer = setTimeout(() => setShowScrollHint(false), 3000);
            }
        };

        // Initial measurement + an rAF follow-up for late-settling content
        check();
        const rafId = requestAnimationFrame(check);

        // Re-measure on container resize (Mux Player size settles, comments load, etc.)
        let ro = null;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(check);
            ro.observe(el);
        }

        return () => {
            if (fadeTimer) clearTimeout(fadeTimer);
            cancelAnimationFrame(rafId);
            if (ro) ro.disconnect();
        };
    }, [video?.id]);

    // Load hls.js for non-Safari browsers
    useEffect(() => {
        if (!video?.hlsUrl) return;
        if (window.Hls) { setHlsReady(true); return; }
        const script = document.createElement('script');
        script.src = HLS_JS_CDN;
        script.onload = () => setHlsReady(true);
        script.onerror = () => setHlsFailed(true);
        document.head.appendChild(script);
    }, [video?.hlsUrl]);

    // Attach HLS stream to video element
    useEffect(() => {
        if (!video?.hlsUrl || !videoRef.current) return;
        const videoEl = videoRef.current;

        // Safari supports HLS natively — just set src
        if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
            videoEl.src = video.hlsUrl;
            if (autoplay) videoEl.play().catch(() => {});
            return;
        }

        // Other browsers: use hls.js
        if (!hlsReady || !window.Hls?.isSupported()) {
            // hls.js not loaded or not supported — fall back to iframe
            setHlsFailed(true);
            return;
        }

        const hls = new window.Hls({
            maxBufferLength: 30,
            maxMaxBufferLength: 60,
            startLevel: -1,              // auto-detect, but...
            autoStartLoad: true,
            capLevelToPlayerSize: true,   // never load higher than the player element size
            testBandwidth: true,
            abrEwmaDefaultEstimate: 5000000, // assume 5 Mbps to start (enough for 720p-1080p)
        });
        hlsRef.current = hls;

        hls.loadSource(video.hlsUrl);
        hls.attachMedia(videoEl);
        hls.on(window.Hls.Events.MANIFEST_PARSED, (event, data) => {
            // Start at the second-highest quality (not max, not min)
            if (data.levels.length > 2) {
                hls.startLevel = data.levels.length - 2;
            } else if (data.levels.length > 1) {
                hls.startLevel = data.levels.length - 1;
            }
            if (autoplay) videoEl.play().catch(() => {});
        });
        hls.on(window.Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
                console.warn('HLS playback failed, falling back to iframe');
                setHlsFailed(true);
            }
        });

        return () => {
            hls.destroy();
            hlsRef.current = null;
        };
    }, [video?.hlsUrl, hlsReady]);

    // ── Auto-complete at 75% (class recordings only) ──
    // Silently flips the completion badge once the viewer has reached the
    // threshold, matching Netflix/Peloton behavior. See
    // docs/decisions/adr-auto-completion.md.
    //
    // Gate (ALL must hold):
    //   - userId — must be a signed-in user (no orphaned completions)
    //   - !isAdmin — admins watch as creators, not participants
    //   - video.isClassRecording — Bunny shorts are out of scope
    //
    // NOTE (event-log model, adr-completions-event-log.md): we deliberately do
    // NOT gate on "already completed". Completions are a session log now — a
    // re-watch to ≥75% SHOULD log a fresh completion so it credits the streak /
    // challenge. Inflation is bounded by (a) one fire per modal-open via
    // autoCompleteFiredRef and (b) the per-session occurrenceKey, which makes
    // the server write idempotent for THIS session while allowing a later one.
    //
    // Mechanism: attach a `timeupdate` listener to whichever player is rendered
    // (mux-player for class recordings, native <video> for HLS classes) and
    // fire once when currentTime/duration crosses the threshold. The fire
    // calls markWorkoutComplete (occurrence-deduped server-side) and onAutoComplete
    // (parent updates local completedIds without a refetch).
    //
    // Reset semantics: autoCompleteFiredRef resets whenever video?.id changes,
    // so each modal-open is its own one-shot session.
    useEffect(() => {
        if (!userId) return;
        if (isAdmin) return;
        if (!video?.isClassRecording) return;

        // Reset for this modal-open. Re-runs of this effect (e.g. video?.id
        // change) will re-attach with a fresh fired ref.
        autoCompleteFiredRef.current = false;

        // Resolve the active media element. mux-player exposes the underlying
        // HTMLMediaElement via .media (see existing usage in LivePlayer.js
        // line 633, 703). Native HLS path uses videoRef directly. Bunny iframe
        // path is excluded because video.isClassRecording is false there.
        const muxEl = muxPlayerRef.current;
        const nativeEl = videoRef.current;

        // Completion is detected from MULTIPLE signals (additive — see Phase A,
        // adr-completions-event-log.md): the element's `timeupdate`, an `ended`
        // event (play-to-end), AND a 5s `currentTime` polling backstop for
        // browsers (older smart-TV browsers, throttled/background tabs) that fire
        // `timeupdate` too sparsely to reliably catch the 75% crossing. mux-player
        // exposes its inner <video> via .media asynchronously, so we resolve the
        // element at read-time rather than capturing it once.
        // Single completion writer — shared by every detection signal below so
        // the one-shot + occurrence key are honored regardless of which trips it.
        const fireComplete = () => {
            if (autoCompleteFiredRef.current) return;
            autoCompleteFiredRef.current = true;
            // Occurrence-deduped server-side — one completion per playback session.
            // `programId || null`: On Demand class recordings pass no programId
            // (→ null, source 'ondemand' — byte-identical to LIVE-015); an in-app
            // recorded PROGRAM video (also isClassRecording, but with a programId)
            // threads to its program with the program source. Mux-played either way.
            markWorkoutComplete(userId, video.id, video.title || '', programId || null, {
                occurrenceKey: sessionOccurrenceKeyRef.current,
                source: programId ? completionSource : 'ondemand',
            }).catch(() => { /* non-fatal; manual log is the fallback */ });
            if (typeof onAutoComplete === 'function') {
                try { onAutoComplete(video.id); } catch (_) { /* defensive */ }
            }
        };
        const onTimeUpdate = (mediaEl) => {
            if (autoCompleteFiredRef.current) return;
            const cur = mediaEl?.currentTime;
            const dur = mediaEl?.duration;
            if (!Number.isFinite(cur) || !Number.isFinite(dur) || dur <= 0) return;
            if ((cur / dur) >= AUTO_COMPLETE_THRESHOLD) fireComplete();
        };

        // Resolve the live media element each read (mux-player's .media is async).
        const resolveEl = () => (muxEl ? muxEl.media : nativeEl);
        // The element that emits the events (mux custom element forwards them).
        const target = muxEl || nativeEl;

        const detachers = [];
        if (target) {
            const tuHandler = () => onTimeUpdate(resolveEl());
            const endedHandler = () => fireComplete(); // play-to-end ⇒ unambiguous completion
            target.addEventListener('timeupdate', tuHandler);
            target.addEventListener('ended', endedHandler);
            detachers.push(() => { try { target.removeEventListener('timeupdate', tuHandler); } catch (_) { /* defensive */ } });
            detachers.push(() => { try { target.removeEventListener('ended', endedHandler); } catch (_) { /* defensive */ } });
        }

        // Polling backstop — PURE local DOM read (currentTime/duration), NO Firebase.
        // Covers older/TV browsers that fire `timeupdate` sparsely or throttle a
        // backgrounded tab, so the 75% crossing is still detected. Self-clears the
        // instant completion fires; one in-memory check every 5s, zero network.
        const pollId = setInterval(() => {
            if (autoCompleteFiredRef.current) { clearInterval(pollId); return; }
            onTimeUpdate(resolveEl());
        }, 5000);
        detachers.push(() => { try { clearInterval(pollId); } catch (_) { /* defensive */ } });

        // If neither element is available (iframe path or pre-mount) the listeners
        // simply never attach; the poll reads null → no-op. iframe = Bunny only.
        return () => detachers.forEach((fn) => { try { fn(); } catch (_) { /* defensive */ } });
    }, [video?.id, video?.isClassRecording, video?.title, isAdmin, userId, onAutoComplete, programId, completionSource]);

    // ── Program (Bunny) auto-complete at 75% ──
    // Parity for program workouts (adr-completions-event-log.md §2.7). Kept as a
    // SEPARATE effect from the class-recording one above (which is untouched, so
    // LIVE-015 can't regress); the two are mutually exclusive via the
    // isClassRecording gate, so the shared autoCompleteFiredRef is safe.
    //
    // Program videos render EITHER as native HLS (timeupdate available) OR a Bunny
    // iframe (Player.js bridge). We attach to whichever exists. Fires ONE
    // occurrence-deduped completion scoped to programId.
    //
    // Gated on BOTH onAutoComplete AND a truthy programId so it runs ONLY for
    // genuine program videos (ProgramView). This is deliberate: Workouts passes
    // onAutoComplete for its On Demand modal too, but with no programId — so free
    // Bunny videos opened there stay manual-only (matches adr-auto-completion's
    // shorts exclusion), and class recordings are handled by the effect above.
    // Graceful: if neither progress source works, the card's manual log remains.
    useEffect(() => {
        if (!userId || isAdmin) return;
        if (video?.isClassRecording) return;        // handled by the effect above
        if (!video?.id) return;
        if (typeof onAutoComplete !== 'function') return;
        if (!programId) return;                      // program videos only (see above)

        autoCompleteFiredRef.current = false;

        const fire = () => {
            if (autoCompleteFiredRef.current) return;
            autoCompleteFiredRef.current = true;
            markWorkoutComplete(userId, video.id, video.title || '', programId || null, {
                occurrenceKey: sessionOccurrenceKeyRef.current,
                source: completionSource,
            }).catch(() => {});
            if (typeof onAutoComplete === 'function') {
                try { onAutoComplete(video.id); } catch (_) { /* defensive */ }
            }
        };

        // Native HLS path (program video with an hlsUrl): timeupdate + ended +
        // a 5s currentTime polling backstop (pure local read, NO Firebase) so
        // sparse-event / TV browsers still trip the 75% mark or the play-to-end.
        const nativeEl = videoRef.current;
        const detachers = [];
        if (nativeEl) {
            const tuHandler = () => {
                if (autoCompleteFiredRef.current) return;
                const cur = nativeEl.currentTime;
                const dur = nativeEl.duration;
                if (Number.isFinite(cur) && Number.isFinite(dur) && dur > 0 && (cur / dur) >= AUTO_COMPLETE_THRESHOLD) fire();
            };
            const endedHandler = () => fire();
            nativeEl.addEventListener('timeupdate', tuHandler);
            nativeEl.addEventListener('ended', endedHandler);
            detachers.push(() => { try { nativeEl.removeEventListener('timeupdate', tuHandler); } catch (_) { /* defensive */ } });
            detachers.push(() => { try { nativeEl.removeEventListener('ended', endedHandler); } catch (_) { /* defensive */ } });
            const pollId = setInterval(() => {
                if (autoCompleteFiredRef.current) { clearInterval(pollId); return; }
                tuHandler();
            }, 5000);
            detachers.push(() => { try { clearInterval(pollId); } catch (_) { /* defensive */ } });
        }

        // Bunny iframe path (program video without an hlsUrl) — Player.js bridge.
        const iframe = iframeRef.current;
        if (iframe) {
            const detachBunny = subscribeBunnyProgress(iframe, (seconds, duration) => {
                if (duration > 0 && (seconds / duration) >= AUTO_COMPLETE_THRESHOLD) fire();
            });
            detachers.push(detachBunny);
        }

        return () => detachers.forEach((fn) => { try { fn(); } catch (_) { /* defensive */ } });
    }, [video?.id, video?.isClassRecording, video?.title, userId, isAdmin, programId, onAutoComplete, hlsFailed, completionSource]);

    // ══════════════════════════════════════════════════════════════════════
    //  ON-DEMAND WATCH PROGRESS TRACKING
    // ══════════════════════════════════════════════════════════════════════
    // Captures cumulative watch time per (user, recording) so the admin
    // class metrics view can show partial watchers in addition to the
    // existing completion list ("Sarah watched 38:22 across 2 sessions").
    //
    // Mirrors the LivePlayer accumulator pattern: refs track playing-state
    // seconds locally, periodic flushes send DELTAS to /api/track-watch
    // which uses Firestore increment() to maintain the cumulative total.
    //
    // Flush triggers (any of):
    //   - 60s of accumulated play time elapsed since last flush
    //   - visibilitychange → hidden (tab switch, mobile background)
    //   - pagehide (final unload)
    //   - effect cleanup (modal close, video?.id change)
    //
    // Reliability: visibilitychange + pagehide flushes use fetch with
    // keepalive: true so the request survives page unload. Without that
    // we'd lose the final delta on every tab close.
    //
    // Gates (ALL must hold; same shape as auto-complete effect above):
    //   - userId — must be a signed-in user (no orphaned writes)
    //   - !isAdmin — admins watch as creators, not participants (also
    //     enforced server-side in /api/track-watch as defense in depth)
    //   - video.isClassRecording — Bunny shorts are out of scope
    //   - getToken — required for the Bearer header (passed in by parent)
    //
    // See docs/decisions/adr-on-demand-watch-tracking.md.
    const watchProgressRef = useRef(0);
    const watchProgressLastTimeupdateAtRef = useRef(0);
    const watchProgressIsPlayingRef = useRef(false);
    const watchProgressIsFirstFlushRef = useRef(true);
    useEffect(() => {
        if (!userId) return;
        if (isAdmin) return;
        if (!video?.isClassRecording) return;
        if (typeof getToken !== 'function') return;
        if (!video?.id) return;

        // Reset all session-scoped refs for this modal-open. video?.id is in
        // the deps array so opening a different recording starts fresh.
        watchProgressRef.current = 0;
        watchProgressLastTimeupdateAtRef.current = 0;
        watchProgressIsPlayingRef.current = false;
        watchProgressIsFirstFlushRef.current = true;
        cumulativeWatchedRef.current = 0; // total watched this open (local + cast)

        const muxEl = muxPlayerRef.current;
        const nativeEl = videoRef.current;

        // Resolve the underlying media element each time we need it.
        // mux-player's inner <video> exists once the script has registered
        // the custom element; reading .media in the handlers (not at effect
        // creation) avoids capturing a stale null reference.
        const getMediaEl = () => {
            if (muxEl && muxEl.media) return muxEl.media;
            return nativeEl || null;
        };

        // Accumulator — fires on every timeupdate. Adds delta from previous
        // timeupdate to the running total IFF the player is playing.
        // Defensive cap at 1.5s prevents runaway accumulation if timeupdate
        // gaps for any reason (e.g. browser background suspension dropping
        // events while keeping the timer alive).
        const onTimeUpdate = () => {
            const now = Date.now();
            const last = watchProgressLastTimeupdateAtRef.current;
            // `&& !castConnectedRef.current`: during a cast session the cast
            // effect owns accumulation, so the local timeupdate (which may still
            // fire under AirPlay) must NOT also accumulate — prevents double-count.
            // When not casting this is exactly today's behavior.
            if (last > 0 && watchProgressIsPlayingRef.current && !castConnectedRef.current) {
                const delta = (now - last) / 1000;
                if (delta > 0 && delta < 1.5) {
                    watchProgressRef.current += delta;
                    cumulativeWatchedRef.current += delta; // total watched (local+cast)
                }
            }
            watchProgressLastTimeupdateAtRef.current = now;
        };
        const onPlay = () => {
            watchProgressIsPlayingRef.current = true;
            watchProgressLastTimeupdateAtRef.current = Date.now();
        };
        const onPauseOrEnded = () => {
            watchProgressIsPlayingRef.current = false;
            // Don't reset lastTimeupdate; if the player resumes, we just
            // continue from now (next timeupdate). The IS_PLAYING gate
            // is what prevents accumulation while paused.
        };

        // Flush helper — sends current delta, resets local accumulator on
        // attempt (regardless of network outcome). isSessionStart is true
        // only on the FIRST flush of this modal-open; the server uses it
        // to increment sessionsCount. Subsequent flushes don't.
        const flush = () => {
            const delta = watchProgressRef.current;
            // HARD FLOOR: require at least 1 second of real playback before
            // any record is created. Without this gate, opening the modal
            // and immediately closing it (without pressing play) would
            // create a noise record with watchedSeconds=0 / sessionsCount=1
            // that would inflate "Reach" and clutter the on-demand viewing
            // list. The user explicitly flagged this on 2026-04-27. The
            // bar for counting toward engagement is "actually pressed play
            // and watched some video," not "looked at the description."
            if (delta < 1) return;
            // Skip writes that contribute below the minimum delta. EXCEPT
            // for the first flush of a session — we want sessionsCount to
            // increment as soon as there's meaningful play (≥1s, gated above).
            if (delta < WATCH_PROGRESS_MIN_DELTA_SEC && !watchProgressIsFirstFlushRef.current) {
                return;
            }
            // Reset the local accumulator BEFORE awaiting — if multiple
            // flushes fire in rapid succession (e.g. heartbeat firing right
            // before visibilitychange), we don't double-count the same delta.
            const isSessionStart = watchProgressIsFirstFlushRef.current;
            watchProgressRef.current = 0;
            watchProgressIsFirstFlushRef.current = false;
            // Fire-and-forget — visibilitychange / pagehide contexts can't
            // reliably await a promise. Errors logged inside the helper.
            flushWatchProgress({
                getToken,
                recordingId: video.id,
                classId: video.classId || null,
                watchedSecondsDelta: delta,
                isSessionStart,
            });
        };

        // Heartbeat: every WATCH_PROGRESS_HEARTBEAT_MS, flush if there's
        // accumulated progress. Throttle is wall-clock; the MIN_DELTA gate
        // inside flush() handles the "user paused for 5 minutes" case.
        const intervalId = setInterval(flush, WATCH_PROGRESS_HEARTBEAT_MS);

        // Visibility flushes — the load-bearing piece for tab-close
        // reliability. visibilitychange fires when tab is backgrounded,
        // mobile app is sent to background, screen lock, etc. pagehide
        // fires on final unload (more reliable than beforeunload on iOS).
        // Both call flush(), which uses fetch keepalive so the request
        // survives the page going away.
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') flush();
        };
        const onPageHide = () => flush();
        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);

        // Listener attachment to the player. Like the auto-complete effect,
        // we attach to whichever element is rendered. Multiple addEventListener
        // calls on the same element coexist — the auto-complete effect's
        // own timeupdate handler fires independently.
        let detach = () => {};
        if (muxEl) {
            muxEl.addEventListener('timeupdate', onTimeUpdate);
            muxEl.addEventListener('play', onPlay);
            muxEl.addEventListener('pause', onPauseOrEnded);
            muxEl.addEventListener('ended', onPauseOrEnded);
            detach = () => {
                try { muxEl.removeEventListener('timeupdate', onTimeUpdate); } catch (_) { /* defensive */ }
                try { muxEl.removeEventListener('play', onPlay); } catch (_) { /* defensive */ }
                try { muxEl.removeEventListener('pause', onPauseOrEnded); } catch (_) { /* defensive */ }
                try { muxEl.removeEventListener('ended', onPauseOrEnded); } catch (_) { /* defensive */ }
            };
        } else if (nativeEl) {
            nativeEl.addEventListener('timeupdate', onTimeUpdate);
            nativeEl.addEventListener('play', onPlay);
            nativeEl.addEventListener('pause', onPauseOrEnded);
            nativeEl.addEventListener('ended', onPauseOrEnded);
            detach = () => {
                try { nativeEl.removeEventListener('timeupdate', onTimeUpdate); } catch (_) { /* defensive */ }
                try { nativeEl.removeEventListener('play', onPlay); } catch (_) { /* defensive */ }
                try { nativeEl.removeEventListener('pause', onPauseOrEnded); } catch (_) { /* defensive */ }
                try { nativeEl.removeEventListener('ended', onPauseOrEnded); } catch (_) { /* defensive */ }
            };
        }
        // If neither ref is available (iframe path), the effect is a no-op.
        // video.isClassRecording === true guarantees one of the two paths
        // above; iframe is only used for Bunny shorts which are excluded.

        // Reference for getMediaEl — silences linter if unused; the
        // accumulator currently doesn't need to read the media element
        // because timeupdate gives us all the timing info we need.
        // Keeping the helper around in case we extend later.
        void getMediaEl;

        // Cleanup runs when:
        //   - effect deps change (e.g. video?.id — different recording)
        //   - component unmounts (modal closes)
        // In both cases, send a final flush so the last bit of progress
        // is captured before we tear down the listeners.
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            detach();
            // Final flush — captures any in-flight playing time. uses
            // regular fetch (no keepalive needed since this is the
            // intentional cleanup path, not page unload).
            flush();
        };
    }, [video?.id, video?.isClassRecording, video?.classId, isAdmin, userId, getToken]);
    // ══════════════════════════════════════════════════════════════════════

    // ── Playing-state tracking for the screen Wake Lock ──
    // Toggles `isPlaying` from the media element's play/pause/ended events so
    // the wake lock (declared near the top) is held only while video is
    // actually playing. Resolves the element the same way the watch-progress
    // effect does (mux-player.media → native <video>). The iframe path (Bunny
    // shorts) exposes no media element here → isPlaying stays false → no lock,
    // which is fine (those clips are short and the lock is best-effort anyway).
    useEffect(() => {
        setIsPlaying(false); // reset on video change / modal open
        const muxEl = muxPlayerRef.current;
        const nativeEl = videoRef.current;
        const el = (muxEl && typeof muxEl.addEventListener === 'function') ? muxEl : nativeEl;
        if (!el) return;

        const onPlay = () => setIsPlaying(true);
        const onStop = () => setIsPlaying(false);
        el.addEventListener('playing', onPlay);
        el.addEventListener('play', onPlay);
        el.addEventListener('pause', onStop);
        el.addEventListener('ended', onStop);
        el.addEventListener('waiting', onStop);

        return () => {
            try { el.removeEventListener('playing', onPlay); } catch (_) { /* defensive */ }
            try { el.removeEventListener('play', onPlay); } catch (_) { /* defensive */ }
            try { el.removeEventListener('pause', onStop); } catch (_) { /* defensive */ }
            try { el.removeEventListener('ended', onStop); } catch (_) { /* defensive */ }
            try { el.removeEventListener('waiting', onStop); } catch (_) { /* defensive */ }
            setIsPlaying(false);
        };
    }, [video?.id]);
    // ══════════════════════════════════════════════════════════════════════

    // ── Cast-aware on-demand tracking + completion (MEASUREMENT-ONLY) ──
    // Mirror of the live cast handling: when the user casts a recording to a TV,
    // the local element goes idle, so BOTH the timeupdate accumulator above AND
    // the position-based auto-complete stop. While a cast session is active we
    // accumulate wall-clock seconds into `watchProgressRef` (flushed by the
    // heartbeat above) and `cumulativeWatchedRef`, capped to the recording's
    // duration so cast-and-walk-away can never exceed 100%. We also fire the SAME
    // idempotent completion when cumulative watched reaches 75% of duration —
    // necessary because the position-based trigger is silent during cast. We
    // only OBSERVE the cast session; never control playback. Additive + cast-
    // gated (no session ⇒ subscribeCastSession never fires ⇒ complete no-op, so
    // the non-cast path is byte-identical). The completion firing is gated on
    // `castEverConnectedRef`, so a NON-cast session's completion stays exactly
    // the position-based path. See docs/decisions/adr-cast-aware-watch-tracking.md.
    useEffect(() => {
        if (!userId) return;
        if (isAdmin) return;
        if (!video?.isClassRecording) return;
        if (!video?.id) return;

        castConnectedRef.current = false;
        castEverConnectedRef.current = false;

        let castTickId = null;
        let lastTickAt = 0;

        const getMediaEl = () => {
            const muxEl = muxPlayerRef.current;
            if (muxEl && muxEl.media) return muxEl.media;
            return videoRef.current || null;
        };
        const durationCap = () => {
            const el = getMediaEl();
            const d = el && el.duration;
            return (Number.isFinite(d) && d > 0) ? d : 0;
        };

        const foldCastElapsed = () => {
            if (lastTickAt <= 0) return;
            const now = Date.now();
            let delta = (now - lastTickAt) / 1000;
            lastTickAt = now;
            if (!(delta > 0 && delta < 3600)) return;
            // Cap cumulative so cast-and-walk-away can't exceed 100% of the video.
            const cap = durationCap();
            if (cap > 0 && cumulativeWatchedRef.current + delta > cap) {
                delta = Math.max(0, cap - cumulativeWatchedRef.current);
            }
            if (delta <= 0) return;
            watchProgressRef.current += delta;
            cumulativeWatchedRef.current += delta;
            // Time-based completion for cast (position-based can't fire). One-shot
            // via the shared autoCompleteFiredRef; idempotent server-side.
            if (
                cap > 0
                && !autoCompleteFiredRef.current
                && cumulativeWatchedRef.current >= cap * AUTO_COMPLETE_THRESHOLD
            ) {
                autoCompleteFiredRef.current = true;
                markWorkoutComplete(userId, video.id, video.title || '', null, {
                    occurrenceKey: sessionOccurrenceKeyRef.current,
                    source: 'ondemand',
                }).catch(() => {});
                if (typeof onAutoComplete === 'function') {
                    try { onAutoComplete(video.id); } catch (_) { /* defensive */ }
                }
            }
        };

        const stopCastTick = () => {
            if (castTickId) { clearInterval(castTickId); castTickId = null; }
            foldCastElapsed(); // capture the final partial interval
            lastTickAt = 0;
        };

        const onCastChange = (isConnected) => {
            if (isConnected) {
                if (castConnectedRef.current) return;
                castConnectedRef.current = true;
                castEverConnectedRef.current = true;
                lastTickAt = Date.now();
                castTickId = setInterval(foldCastElapsed, 10000);
            } else {
                if (!castConnectedRef.current) return;
                castConnectedRef.current = false;
                stopCastTick();
            }
        };

        const unsubscribe = subscribeCastSession(getMediaEl, onCastChange);

        return () => {
            unsubscribe();
            stopCastTick();
            castConnectedRef.current = false;
        };
    }, [video?.id, video?.isClassRecording, video?.title, userId, isAdmin, onAutoComplete]);
    // ══════════════════════════════════════════════════════════════════════

    if (!video) return null;
    const isMuxRecording = video.isClassRecording && video.muxPlaybackId;
    const useNativePlayer = !isMuxRecording && video.hlsUrl && !hlsFailed;
    const isDriveVideo = !!video.streamUrl;
    const groupLabel = (g) => ({ upper: 'Upper Body', lower: 'Lower Body', core: 'Core' }[g] || g);

    return (
        <div className="mom-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'var(--mom-overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
            <div ref={scrollContainerRef} className="modal-scroll" style={{ background: 'var(--mom-white)', borderRadius: 24, maxWidth: vertical ? 480 : 800, width: '100%', overflow: 'hidden', animation: 'modalIn 0.3s ease-out', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ position: 'relative', background: 'var(--mom-charcoal)' }}>
                    <button onClick={onClose} className="mom-btn" style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.5)', border: 'none', color: 'var(--mom-white)', width: 36, height: 36, borderRadius: '50%', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, backdropFilter: 'blur(8px)' }}>✕</button>

                    {isMuxRecording ? (
                        /* Mux Player for class recordings — AirPlay + Chromecast built in */
                        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                            <mux-player
                                ref={muxPlayerRef}
                                playback-id={video.muxPlaybackId}
                                stream-type="on-demand"
                                {...(autoplay ? { autoplay: '' } : {})}
                                playsinline
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', '--media-object-fit': 'cover' }}
                            />
                        </div>
                    ) : useNativePlayer ? (
                        /* Native <video> with HLS — enables AirPlay + Chromecast */
                        <div style={{ position: 'relative', paddingBottom: vertical ? '177.78%' : '56.25%', height: 0, overflow: 'hidden' }}>
                            <video
                                ref={videoRef}
                                controls
                                playsInline
                                autoPlay={autoplay}
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: '#000' }}
                                x-webkit-airplay="allow"
                                webkit-playsinline="true"
                            />
                        </div>
                    ) : isDriveVideo ? (
                        /* Fallback: Bunny Stream iframe embed */
                        <div style={{ position: 'relative', paddingBottom: vertical ? '177.78%' : '56.25%', height: 0, overflow: 'hidden' }}>
                            <iframe ref={iframeRef} src={video.streamUrl} title={video.title} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen loading="lazy" />
                        </div>
                    ) : (
                        /* No video source */
                        <div style={{ height: 360, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--mom-white)', background: 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))' }}>
                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 32, fontWeight: 700, fontStyle: 'italic', textAlign: 'center', lineHeight: 1.2 }}>moments of movement</span>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>with Danielle</span>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 20 }}>Video unavailable</p>
                        </div>
                    )}
                </div>
                <div className="mom-modal-body" style={{ padding: '24px 32px 28px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                        {/* Duration pill. Suppressed only where the caller opts out
                            (a challenge whose stated workout time lives in the
                            description, not the file length) — default true, so every
                            other surface is unchanged. */}
                        {showLength && (
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: 'var(--mom-cream)', color: 'var(--mom-charcoal)' }}>{video.length} min</span>
                        )}
                        {video.groups.map((g) => (
                            <span key={g} style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 20, background: 'var(--mom-cream)', color: 'var(--mom-primary)' }}>{groupLabel(g)}</span>
                        ))}
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {/* preRelease: a release-day class played from the Schedule before
                                it's filed into On Demand — the public class page can't open it
                                yet, so there's no link to share (services/classRelease.js). */}
                            {video.isClassRecording && video.id && !video.isProgramVideo && !video.preRelease && (
                                <button onClick={handleShareLink} aria-label="Copy a shareable link to this class"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                        <polyline points="16 6 12 2 8 6" />
                                        <line x1="12" y1="2" x2="12" y2="15" />
                                    </svg>
                                    {copied && (
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>Copied</span>
                                    )}
                                </button>
                            )}
                            {onToggleFavorite && (
                                <button onClick={onToggleFavorite}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill={isFavorited ? 'var(--mom-primary)' : 'none'} stroke="var(--mom-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.2s ease' }}>
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                    </svg>
                                    {favoriteCount > 0 && (
                                        <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-primary)' }}>{favoriteCount}</span>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                    <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, marginBottom: 10, color: 'var(--mom-charcoal)' }}>{video.title}</h2>
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.7 }}>{video.description}</p>
                    {/* Optional community slot (release-day classes — ClassTogether.js).
                        Absent for every other caller, so nothing renders. */}
                    {typeof renderTogether === 'function' && renderTogether({ completionCount, openComments })}
                    {/* Manual "Log this workout" + history (event-log model). Shown for
                        class recordings to signed-in users (the parent that wires
                        onAutoComplete). The fallback when ≥75% auto-credit misses. */}
                    {userId && video.isClassRecording && typeof onAutoComplete === 'function' && (
                        <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                            {manualLogged ? (
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-primary)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span>✓</span> {completionCopy.loggedLabel || 'Logged'}
                                </span>
                            ) : (
                                <button onClick={handleManualLog} className="mom-btn" style={{ padding: '9px 18px', borderRadius: 12, fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600, border: '1.5px solid var(--mom-primary)', background: 'transparent', color: 'var(--mom-primary)', cursor: 'pointer' }}>
                                    {completionCount > 0 ? (completionCopy.logAgainLabel || 'Log again') : (completionCopy.logLabel || 'Log this workout')}
                                </button>
                            )}
                            {completionCount > 0 && lastCompletedAt && (
                                <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
                                    {completionCount > 1 ? (
                                        <>
                                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 15, color: 'var(--mom-primary)' }}>{completionCount}×</span>
                                            <span>· last {relativeDayLabel(lastCompletedAt)}</span>
                                        </>
                                    ) : (
                                        <span>Completed · {relativeDayLabel(lastCompletedAt)}</span>
                                    )}
                                </span>
                            )}
                        </div>
                    )}
                    {/* Persistent "Your history" — per-entry completion removal.
                        Renders for any logged video (class recording OR program)
                        once the parent wires onUndoComplete. No resting clutter:
                        gated on having ≥1 completion. See docs/ux/completion-undo.md. */}
                    {userId && completionCount > 0 && typeof onUndoComplete === 'function' && (
                        <div style={{ marginTop: 12 }}>
                            <button onClick={toggleHistory} className="mom-btn"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-warm-gray)', textDecoration: 'underline', padding: 0 }}>
                                {(completionCopy.historyDisclosureLabel || 'Your history')} {historyOpen ? '▴' : '▾'}
                            </button>
                            {historyOpen && (
                                <CompletionHistoryList
                                    sessions={historyList}
                                    loading={historyLoading}
                                    copy={completionCopy}
                                    onRemove={handleRemoveEntry}
                                />
                            )}
                        </div>
                    )}
                    {/* Post-class comments — shown on class recordings (On Demand).
                        Excluded for in-app recorded PROGRAM videos: their id is a
                        programVideos doc id (not a class), and program videos have
                        no comment thread (parity with Bunny program videos). */}
                    {video.isClassRecording && (video.classId || video.id) && !video.isProgramVideo && (
                        <div ref={commentsWrapRef}>
                        <ClassComments
                            classId={video.classId || null}
                            recordingId={video.id || null}
                            userId={userId}
                            userName={userName}
                            isAdmin={isAdmin}
                            commentCount={video.commentCount || 0}
                            getToken={getToken}
                            highlightCommentId={highlightCommentId}
                        />
                        </div>
                    )}
                </div>
                {/* Scroll hint - fades out after 3s */}
                {showScrollHint && (
                    <div style={{ position: 'sticky', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'center', paddingBottom: 10, pointerEvents: 'none', animation: 'hintFade 3s ease forwards' }}>
                        <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: 20, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)', letterSpacing: '0.05em' }}>scroll for details</span>
                            <span style={{ fontSize: 12, color: 'var(--mom-warm-gray)', animation: 'bounceDown 1.2s ease infinite' }}>↓</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default VideoPlayerModal;
